<template>
  <div id="app" :class="wrapperClasses" @click="collapseOverlays">
    <header class="top-nav">
      <nav class="inner nav-container">
        <h1 class="site-title selectable" @click="showHome" :title="main.strapline">{{main.title}}</h1>
        <ul class="menu">
          <li v-for="item in mainMenu" :key="item.to">
            <router-link :to="item.to">{{item.title}}</router-link>
          </li>
        </ul>
        <div v-if="hasCart" class="cart-widget" :class="{'show-cart': showCartWidget}">
          <div class="toggle-cart selectable" @click="toggleCart">
            <div class="icon-basket"></div>
            <span class="num" :title="total|currency">{{numCartItems}}</span>
          </div>
          <cart>
            <div class="open-checkout selectable" @click="openCheckout">
              <span class="label">{{labels.checkout}}</span>
            </div>
          </cart>
        </div>
        <div class="icon-menu selectable toggle-menu" @click="toggleMenu"></div>
      </nav>
      <div class="inner logo-container selectable" @click="showHome"></div>
    </header>
    <main class="main">
      <keep-alive>
        <router-view />
      </keep-alive>
      <page-footer></page-footer>
    </main>
  </div>
</template>

<script>
import Cart from "./components/Cart.vue";
import PageFooter from "./components/PageFooter.vue";
import { mapGetters, mapActions } from "vuex";
import filters from "./mixins/filters";
import { hasEcwidCart, getCart } from "./api/ecwid";

export default {
  name: "app",
  components: {
    Cart,
    PageFooter,
  },
  mixins: [filters],
  computed: {
    ...mapGetters([
      "main",
      "cart",
      "labels",
      "total",
      "mainMenu",
      "numCartItems",
      "hasCart",
    ]),
    wrapperClasses() {
      let cls = ["wrapper"];
      const parts = this.$route.path.replace(/(^\/|\/$)/g, "").split("/");
      let section = "home";
      let sub = "";
      if (parts.length > 0) {
        if (parts[0].length > 1) {
          section = parts.shift();
        }
        if (parts.length > 0) {
          if (parts[0].length > 1) {
            sub = parts.join("--");
          }
        }
      }
      const hasSub = sub.length > 1;
      cls.push("section-" + section);
      if (hasSub) {
        cls.push("sub-" + sub);
      }
      switch (section) {
        case "home":
          cls.push("show-main");
          break;
        case "products":
          if (hasSub) {
            cls.push("show-detail");
          }
          break;
        default:
          cls.push("show-detail");
          break;
      }
      if (this.showMenu) {
        cls.push("show-menu");
      }
      return cls;
    },
  },
  data() {
    return {
      showCartWidget: false,
      showMenu: false,
      pagesLoaded: false,
    };
  },
  watch: {
    "$route.path"(path, prevPath) {
      this.showMenu = false;
      let xPath = "body";
      let ts = 0;
      let prevWasProduct = prevPath.indexOf("/products/") >= 0;
      if (path.indexOf("/products/") === 0) {
        xPath = "#" + this.pathToItemId(path, "os");
        if (prevWasProduct) {
          ts = 1255;
        }
      } else if (prevWasProduct) {
        ts = 255;
      }
      let target = document.querySelector(xPath);
      if (target) {
        setTimeout(() => {
          this.$scrollTo(xPath, 500);
        }, ts);
      }
    },
  },
  created() {
    this.getSiteData()
      .then((data) => {
        if (data.pages) {
          if (data.pages.length > 0) {
            this.pagesLoaded = true;
          }
        }
        if (data.cached) {
          setTimeout(() => {
            this.getSiteData(false);
          }, 5 * 60 * 1000);
        }
      })
      .catch((e) => {});
    setTimeout(() => {
      this.getCart();
    }, 5000);

    window.addEventListener("keydown", this.handleKeyDown);
  },
  methods: {
    ...mapActions(["getSiteData", "updateCart"]),
    openCheckout() {
      if (hasEcwidCart()) {
        getCart().gotoCheckout();
      }
    },
    getCart() {
      if (hasEcwidCart()) {
        getCart().get((data) => {
          this.updateCart(data);
        });
        window.Ecwid.OnCartChanged.add((data) => {
          this.updateCart(data);
        });
        window.Ecwid.OnOrderPlaced.add(() => {
          this.updateCart({
            id: 0,
            items: [],
          });
        });
      }
    },
    showCart() {
      if (this.hasCart) {
        this.showCartWidget = true;
        this.showMenu = false;
      }
    },
    showHome() {
      if (this.$route.path !== "/path") {
        this.$router.push("/");
      }
    },
    hideCart() {
      this.showCartWidget = false;
    },
    toggleCart() {
      if (!this.showCartWidget) {
        this.showCart();
      } else {
        this.hideCart();
      }
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
      if (this.showMenu) {
        this.showCartWidget = false;
      }
    },
    handleKeyDown(e) {
      switch (e.keyCode) {
        case 27:
          this.showCartWidget = false;
          this.showMenu = false;
          break;
      }
    },
    collapseOverlays(e) {
      if (this.showHome || this.showCart) {
        let collapse = false;
        let { classList, tagName } = e.target;
        switch (tagName.toLowerCase()) {
          case "h1":
            collapse = classList.contains("site-title");
            break;
          case "h2":
            collapse = true;
            break;
          case "h3":
            collapse = classList.contains("strapline");
            break;
          case "li":
            collapse = classList.contains("contracted");
            break;
          case "div":
            collapse =
              classList.contains("wrapper") || classList.contains("inner");
            break;
          case "main":
          case "header":
          case "footer":
          case "body":
            collapse = true;
            break;
        }
        if (collapse) {
          this.showMenu = false;
          this.hideCart();
        }
      }
    },
    clearContentCache() {
      this.getSiteData(false);
    },
  },
};
</script>
<style lang="scss">
@import "./styles/app.scss";
</style>
